// Outlook add-in: attach this email (.eml + files) to an NDT booking's report attachments.
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Button, Card, CardBody, CardHeader, Label } from "reactstrap";
import Select from "react-select";
import { PacmanLoader } from "react-spinners";
import axios from "axios";
import _ from "lodash";

import NotAuthorized from "modules/5_view_helpers/web/NotAuthorized";
import HasRole from "modules/5_view_helpers/HasRole";
import { Base64toFileObject } from "modules/5_view_helpers/FileFunctions";
import { getProjectQaNdtBookings, updateProjectQaNdtBooking } from "../actions";

function mapStateToProps(state) {
  return {
    ndt_bookings: state.project_qa_ndt_bookings,
  };
}

const bookingLabel = (booking) => {
  const date = booking.date ? moment(booking.date).format("DD/MM/YYYY") : "";
  return [booking.po_number, booking.description, date].filter(Boolean).join(" — ");
};

const normalizeMessageId = (id) => String(id || "").replace(/[<>\s]/g, "");

const messageIdsFromHeaders = (headers, internetMessageId) => {
  const ids = [];
  if (internetMessageId) ids.push(internetMessageId);
  const text = headers || "";
  const angle = /<([^>]+)>/g;
  let match;
  while ((match = angle.exec(text)) !== null) {
    ids.push(match[1]);
  }
  return _.uniq(_.compact(_.map(ids, normalizeMessageId)));
};

const fileFromBase64 = (base64, name, type) => {
  const file = Base64toFileObject(base64, name, type);
  if (!(file instanceof File)) return null;
  return { attachment: file, name: file.name };
};

const ImportNdtBookingReportOutlook = (props) => {
  const { history, ndt_bookings } = props;
  const updateRole = HasRole({ resource_type: "Project::Qa::NdtBooking", name: ["update", "moderator"] }, false, true);
  const [loading, setLoading] = useState(true);
  const [loadingText, setLoadingText] = useState("Reading email...");
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [matched, setMatched] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    props.getProjectQaNdtBookings(true);

    const item = typeof Office !== "undefined" && Office.context?.mailbox?.item;
    if (!item) {
      setError("No Outlook email selected");
      setLoading(false);
      return;
    }

    const reads = [];

    reads.push(
      new Promise((resolve) => {
        item.getAsFileAsync((result) => {
          if (result.status !== Office.AsyncResultStatus.Succeeded) return resolve(null);
          resolve(fileFromBase64(result.value, "original_email.eml", "message/rfc822"));
        });
      })
    );

    _.each(item.attachments, (attachment) => {
      if (attachment.attachmentType != Office.MailboxEnums.AttachmentType.File) return;
      reads.push(
        new Promise((resolve) => {
          item.getAttachmentContentAsync(attachment.id, (result) => {
            if (result.status !== Office.AsyncResultStatus.Succeeded) return resolve(null);
            resolve(fileFromBase64(result.value.content, attachment.name, attachment.contentType));
          });
        })
      );
    });

    const headersRead = new Promise((resolve) => {
      if (!item.getAllInternetHeadersAsync) return resolve("");
      item.getAllInternetHeadersAsync((result) => {
        resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : "");
      });
    });

    Promise.all([...reads, headersRead]).then((results) => {
      const headers = results.pop();
      setFiles(_.compact(results));

      const messageIds = messageIdsFromHeaders(headers, item.internetMessageId);
      if (_.isEmpty(messageIds)) {
        setLoading(false);
        return;
      }

      axios
        .get("/project/qa/ndt_bookings/find_by_message.json", { params: { message_ids: messageIds } })
        .then((res) => {
          if (res.data?.id) {
            setSelected({ value: res.data.id, label: bookingLabel(res.data), booking: res.data });
            setMatched(true);
          }
        })
        .finally(() => setLoading(false));
    });
  }, []);

  const options = _.orderBy(_.values(ndt_bookings), "date", "desc").map((booking) => ({
    value: booking.id,
    label: bookingLabel(booking),
    booking,
  }));

  const importReport = () => {
    if (!selected?.value || _.isEmpty(files) || saving) return;
    setSaving(true);
    setLoadingText("Saving report attachments...");
    props
      .updateProjectQaNdtBooking(selected.value, {
        report_attachments_attributes: _.map(files, (file) => ({ attachment: file.attachment })),
      })
      .then(() => {
        toastr.success("Imported NDT booking report");
        history.push("/");
        try {
          Office.context.ui.closeContainer();
        } catch (e) {
          // add-in already closing
        }
      })
      .catch((err) => {
        setSaving(false);
        toastr.error(_.get(err, "response.data.error") || "Could not import report");
      });
  };

  if (!updateRole) {
    return <NotAuthorized />;
  }

  if (loading || saving) {
    return (
      <div className="text-center">
        <PacmanLoader />
        <div className="mt-2">{loadingText}</div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <h3>Import NDT Booking Report</h3>
      </CardHeader>
      <CardBody>
        {error && <div className="alert alert-warning">{error}</div>}
        {matched && selected && (
          <div className="alert alert-success">Matched booking from the original NDT email: {selected.label}</div>
        )}
        <Label>NDT Booking</Label>
        <Select
          options={options}
          value={selected}
          onChange={(option) => {
            setSelected(option);
            setMatched(false);
          }}
          placeholder="Select NDT booking"
          isClearable
        />
        <div className="mt-3">
          <b>Files to import</b>
          {_.isEmpty(files) ? (
            <div className="text-muted">None</div>
          ) : (
            <ul className="mb-0">
              {_.map(files, (file) => (
                <li key={file.name}>{file.name}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="mt-3">
          <Button color="success" className="w-100" disabled={!selected?.value || _.isEmpty(files)} onClick={importReport}>
            Import
          </Button>
          <Button className="w-100 mt-2" onClick={() => history.push("/")}>
            Cancel
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

export default connect(mapStateToProps, { getProjectQaNdtBookings, updateProjectQaNdtBooking })(ImportNdtBookingReportOutlook);
